import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Flex, useTheme } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { TimelineSelect } from 'components/TimelineSelect/TimelineSelect';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useTimeBoundTimePeriods } from 'screens/home/summary/components/Cards/Goals/hooks';
import { okrsPageSelector } from 'selectors/okrsPage';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { Archive, Download, ExpandAllListStroke, Refresh2 } from 'assets/assetsV1/iconComponents';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE, OKR_TYPES, TOAST_STATUSES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { getPersistenceKey } from 'constants/helper';
import { sendPageViewEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { AgGridSearch } from 'uiToolkitV1/AgGridWrapperV2';
import { AgGridEllipsisMenu } from 'uiToolkitV1/AgGridWrapperV2/components/EllipsisMenu/AgGridEllipsisMenu';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { TextHeader2 } from 'uiToolkitV1/Typography';

import { convertToCSV, parseCSV } from '../Insights/helpers';
import { setIsArchivedView } from '../slice/GoalsSlice';
import { ArchivedGoalsTable } from './ArchivedGoalsTable';
import { PermanentlyDeleteOKRConfirmationModal } from './PermanentlyDeleteOKRConfirmationModal';
import { UnarchiveOKRConfirmationModal } from './UnarchiveOKRConfirmationModal';
import { usePermanentlyDeleteOkrConfirmationModalLogic } from './usePermanentlyDeleteOkrConfirmationModalLogic';
import { useUnarchiveOkrConfirmationModalLogic } from './useUnarchiveOkrConfirmationModalLogic';

type Props = {
  showArchivedGoals: boolean;

  renderBorderlessTable?: boolean;
};

export const ArchivedGoals = ({ renderBorderlessTable = false, showArchivedGoals }: Props) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: timelineData } = useTimeBoundTimePeriods();

  const { selectedTimelines } = useSelector(okrsPageSelector);

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
  const [selectedTimeline, setSelectedTimeline] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const gridRef = useRef();

  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.GOAL;
    const viewId = null;
    const tabId = null;
    const uniqueId = 'ArchivedGoals';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  const {
    isConfirmationModalOpen,
    isUnarchiveObjectiveConfirmationModal,
    handleOpenConfirmationModal,
    handleCloseConfirmationModal,
    handleUnarchiveOkr,
  } = useUnarchiveOkrConfirmationModalLogic();

  const {
    isPermanentlyDeleteConfirmationModalOpen,
    isPermanentlyDeleteObjectiveConfirmationModal,
    handleOpenPermanentlyDeleteConfirmationModal,
    handleClosePermanentlyDeleteConfirmationModal,
    handlePermanentlyDeleteOkr,
  } = usePermanentlyDeleteOkrConfirmationModalLogic();

  const generateExploreCSVData = useCallback(
    ({ exportedRows }) => {
      const data = gridRef?.current?.api.getDataAsCsv({
        // fileName: DEFAULT_GROUP_BY_INDIVIDUAL_FILE_NAME,
        allColumns: true,
        processCellCallback(params) {
          const { value, node, column } = params ?? {};

          if (column?.colId === 'ag-Grid-AutoColumn') {
            return value?.title;
          }

          if (column?.colId === 'type') {
            if (value === OKR_TYPES.OBJECTIVE) {
              return modifyString(objectivesName, StringModifier.TITLE_CASE);
            }
            return modifyString(keyResultsName, StringModifier.TITLE_CASE);
          }

          if (column?.colId === 'table-team') {
            return value
              ?.map((item) => item)
              ?.filter((item) => item)
              ?.join(` `);
          }

          if (column?.colId === 'table-tags') {
            return value
              ?.map((item) => item)
              ?.filter((item) => item)
              ?.join(` `);
          }

          if (['table-start-date', 'table-due-date', 'archivedDate'].includes(column?.colId)) {
            return value ? format(new Date(value), 'MMM dd yyyy') : 'Never';
          }
          if (['table-owner', 'table-collaborators'].includes(column?.colId)) {
            const { toIds } = node.data;

            if (column?.colId === 'table-owner') {
              return toIds
                ?.map(({ email, ownerType }) => {
                  if (ownerType === 'OWNER') return email;
                  return null;
                })
                ?.filter((item) => item)
                ?.join(` `);
            }
            if (column?.colId === 'table-collaborators') {
              return toIds
                ?.map(({ email, ownerType }) => {
                  if (ownerType === 'COLLABORATOR') return email;
                  return null;
                })
                ?.filter((item) => item)
                ?.join(` `);
            }
          }

          return value;
        },

        exportedRows,
      });

      const parsedCsv = parseCSV(data);

      const updatedCsv = parsedCsv.map((row) => {
        delete row['""\r'];

        return row;
      });

      const csvData = convertToCSV(updatedCsv);
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${moduleName}_Explore_${Date.now()}.csv`;
      a.click();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridRef, moduleName]
  );

  useEffect(
    () => () => {
      dispatch(setIsArchivedView({ isArchivedView: false }));
    },
    [dispatch]
  );

  const refreshCascadedViewData = useCallback(async () => {
    if (isRefreshing) {
      return;
    }
    try {
      setIsRefreshing(true);
      await queryClient.invalidateQueries([REACT_QUERY_KEYS.archivedOkrs]);
    } catch (error) {
      toast({
        title: 'Error reloading. Please try again.',
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
      console.error('Error reloading cascaded view data');
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, queryClient, toast]);

  const handleSelect = useCallback((timeline) => {
    setSelectedTimeline(timeline);
  }, []);

  useEffect(() => {
    if (renderBorderlessTable) {
      setSelectedTimeline(selectedTimelines?.[0]);
    } else {
      setSelectedTimeline(timelineData?.current);
    }
  }, [timelineData, renderBorderlessTable, selectedTimelines]);

  useEffect(() => {
    sendPageViewEvent({
      eventProperties: { page: 'ArchivedGoals', type: 'None', source: 'None' },
    });
  }, []);

  return (
    <Box mt={1}>
      {checkNewNavEnabled() || renderBorderlessTable ? null : (
        <Flex alignItems="center" as="h1" px={4} bgColor={theme.colors.surface.F5F7F9}>
          <TextHeader2 bold py={4} ml={!checkNewNavEnabled() ? 0 : 3} mr="8px">
            {`Archived ${modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)}`} for
          </TextHeader2>
          <Box as="span" fontSize="20px" fontWeight={600}>
            <TimelineSelect selectedTimeline={selectedTimeline} onSelect={handleSelect} />
          </Box>
        </Flex>
      )}

      <PaperWithShadow
        m={checkNewNavEnabled() || renderBorderlessTable ? 0 : 4}
        p={renderBorderlessTable ? 0 : 3}
        borderRadius={renderBorderlessTable ? '0px' : '8px'}
        bgColor="white"
        boxShadow={renderBorderlessTable ? 'none' : theme.shadows.paper}
      >
        <Flex alignItems="center" justifyContent="space-between" pb={2}>
          <Box w="300px">
            <AgGridSearch gridRef={gridRef} placeholder="Search across type, archived by etc." cohortId={null} />
          </Box>

          <Flex alignItems="center">
            <AgGridEllipsisMenu
              options={[
                {
                  label: `Expand/ collapse all ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`,
                  type: 'subMenu',
                  onClick: () => {},
                  subOptions: [
                    {
                      label: `Expand all ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`,
                      onClick: () => gridRef.current.api.expandAll(),
                    },
                    {
                      label: `Collapse all ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`,
                      onClick: () => gridRef.current.api.collapseAll(),
                    },
                  ],
                  Icon: ExpandAllListStroke,
                },
                {
                  label: 'Refresh list',
                  onClick: refreshCascadedViewData,
                  Icon: Refresh2,
                },
                {
                  label: 'Download report',
                  Icon: Download,
                  onClick: generateExploreCSVData,
                },
                {
                  label: `View archived ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`,
                  onClick: () => dispatch(setIsArchivedView({ isArchivedView: !showArchivedGoals })),
                  type: 'radio',
                  value: 'showArchivedGoals',
                  Icon: Archive,
                },
              ]}
              selectedRadioValue={showArchivedGoals ? 'showArchivedGoals' : null}
            />
          </Flex>
        </Flex>
        <ArchivedGoalsTable
          gridStatePersistenceKey={gridStatePersistenceKey}
          selectedTimeline={selectedTimeline}
          gridRef={gridRef}
          openUnarchiveConfirmationModal={handleOpenConfirmationModal}
          openPermanentlyDeleteConfirmationModal={handleOpenPermanentlyDeleteConfirmationModal}
        />
      </PaperWithShadow>
      <UnarchiveOKRConfirmationModal
        isObjective={isUnarchiveObjectiveConfirmationModal}
        handleUnarchiveOkr={handleUnarchiveOkr}
        isOpen={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
      />
      <PermanentlyDeleteOKRConfirmationModal
        isObjective={isPermanentlyDeleteObjectiveConfirmationModal}
        handleUnarchiveOkr={handlePermanentlyDeleteOkr}
        isOpen={isPermanentlyDeleteConfirmationModalOpen}
        onClose={handleClosePermanentlyDeleteConfirmationModal}
      />
    </Box>
  );
};
